// M004-F0747: the claimant's claim flow for the selected envelope, kept out of EnvelopesWorkspace.js. It owns: the review panel's
// open state, the claim detail (the reviewer's reason, envelope.claim.get) for a returned / rejected claim, and the acknowledgement
// (envelope.claim.ack) - sent once the reason is ON SCREEN, never before - and it reports the "needs attention" count upward.
// It returns `claimUi` for EnvelopePane: { button, banner, panel }. No approve / return control exists on the claimant's side.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { envelopeApi } from '../../services/envelopeApi';
import ClaimBanner from './ClaimBanner';
import ClaimReviewPanel from './ClaimReviewPanel';
import { attentionCount, claimBanner, claimPatch, isResubmit, needsAttention, offersSubmit, submitClaimReason } from './claimModel';
import { extractEnvelopeError } from './envelopeModel';

const unwrap = (response) => response?.data?.data || {};
const REASON_STATES = ['returned', 'rejected'];

export default function useClaimFlow({
  orgId, organisations, envelopes, selected, selectedId, ready, receipts, offline, applyRecord, removeEnvelope, reload, say, setToast, showIds, onAttentionCount,
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [claims, setClaims] = useState({}); // envelope_id -> { status: 'loading' | 'ready' | 'error', data, error }
  const buttonRef = useRef(null);
  const acking = useRef(new Set());
  const reading = useRef(new Set());
  const orgRef = useRef(orgId);
  orgRef.current = orgId;

  const claim = selected?.claim || null;
  const claimState = claim?.state || '';
  const wantsReason = REASON_STATES.includes(claimState);

  // Another envelope or organisation ends the panel.
  useEffect(() => { setPanelOpen(false); }, [selectedId, orgId]);

  // Tell the page how many returned claims are waiting (the My Envelopes tab shows it).
  const count = attentionCount(envelopes);
  useEffect(() => { if (typeof onAttentionCount === 'function' && orgId) onAttentionCount(orgId, count); }, [count, orgId, onAttentionCount]);

  const loadClaim = useCallback(async (forOrg, envelopeId) => {
    if (reading.current.has(envelopeId)) return; // one read per envelope at a time
    reading.current.add(envelopeId);
    setClaims((current) => ({ ...current, [envelopeId]: { status: 'loading', data: null, error: null } }));
    try {
      const data = unwrap(await envelopeApi.claimGet(forOrg, envelopeId));
      if (orgRef.current !== forOrg) return;
      setClaims((current) => ({ ...current, [envelopeId]: { status: 'ready', data, error: null } }));
    } catch (cause) {
      if (orgRef.current !== forOrg) return;
      setClaims((current) => ({ ...current, [envelopeId]: { status: 'error', data: null, error: extractEnvelopeError(cause) } }));
    } finally {
      reading.current.delete(envelopeId);
    }
  }, []);

  // A returned / rejected claim: read the reason. Any other state drops the cached reason, so a second return never shows the first one's.
  useEffect(() => {
    if (!orgId || !selectedId) return;
    if (!wantsReason) {
      setClaims((current) => (current[selectedId] ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== selectedId)) : current));
      return;
    }
    if (offline) return;
    if (claims[selectedId]) return;
    loadClaim(orgId, selectedId);
  }, [orgId, selectedId, wantsReason, offline, claims, loadClaim]);

  // Acknowledge once the reviewer's reason is displayed (design 8.0: opening the banner sends envelope.claim.ack).
  const entry = selectedId ? claims[selectedId] : null;
  const reasonShown = Boolean(entry && entry.status === 'ready' && String(entry.data?.reason || '').trim());
  const attention = needsAttention(selected);
  useEffect(() => {
    if (!attention || !reasonShown || offline || !orgId || !selectedId) return;
    if (acking.current.has(selectedId)) return;
    acking.current.add(selectedId);
    const forOrg = orgId;
    const forId = selectedId;
    envelopeApi.claimAck(forOrg, forId).then((response) => {
      const patch = claimPatch(unwrap(response));
      if (patch && orgRef.current === forOrg) applyRecord({ envelope_id: forId, ...patch });
    }).catch(() => { /* the badge stays; the next open tries again */ }).finally(() => { acking.current.delete(forId); });
  }, [attention, reasonShown, offline, orgId, selectedId, applyRecord]);

  const orgName = (id) => {
    const found = (organisations || []).find((org) => String(org.id) === String(id));
    return found ? (found.name || found.organisation_name) : '';
  };

  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => { if (buttonRef.current) buttonRef.current.focus(); }, 0);
  };

  const onSubmitted = (view, target) => {
    setPanelOpen(false);
    const name = selected?.name || 'The envelope';
    const patch = claimPatch(view);
    if (String(target) !== String(orgId)) {
      removeEnvelope(selectedId);
      const where = orgName(target) || `organisation ${target}`;
      setToast(`"${name}" was submitted to ${where}. It now lives in that organisation's Ledger.`);
      say(`${name} submitted to ${where}.`);
    } else {
      if (patch) applyRecord({ envelope_id: selectedId, ...patch });
      say(`${name} submitted as an expense claim. The envelope is locked.`);
    }
    reload();
    setTimeout(() => { const heading = document.getElementById('ew-pane-title'); if (heading) heading.focus(); }, 0);
  };

  const editAndResubmit = () => {
    const first = document.querySelector('[data-testid="envelope-receipts"] [role="option"]');
    if (first) first.focus(); else { const heading = document.getElementById('ew-pane-title'); if (heading) heading.focus(); }
    say('Edit the receipts the reviewer asked about, then choose Resubmit claim.');
  };

  const banner = selected && !selected.pending ? claimBanner(selected, entry?.data) : null;
  const reason = selected ? submitClaimReason(selected, { offline, receiptCount: receipts.length }) : '';
  const resubmit = isResubmit(selected);
  const showButton = Boolean(selected) && !selected.pending && ready && offersSubmit(selected);

  return {
    panelOpen,
    claimUi: {
      banner: banner ? (
        <ClaimBanner
          banner={banner}
          orgName={orgName(claim?.org_id)}
          loading={wantsReason && (!entry || entry.status === 'loading')}
          loadError={entry?.status === 'error' ? entry.error : null}
          onRetry={() => loadClaim(orgId, selectedId)}
          onEditResubmit={editAndResubmit}
          offline={offline}
        />
      ) : null,
      panel: panelOpen && selected ? (
        <ClaimReviewPanel
          key={selected.envelope_id}
          orgId={orgId}
          envelope={selected}
          receipts={receipts}
          organisations={organisations}
          resubmit={resubmit}
          offline={offline}
          onCancel={closePanel}
          onShowIssue={showIds}
          onSubmitted={onSubmitted}
        />
      ) : null,
      button: showButton ? {
        ref: buttonRef,
        label: `${resubmit ? 'Resubmit' : 'Submit'} claim…`,
        reason,
        expanded: panelOpen,
        onClick: () => setPanelOpen((open) => !open),
      } : null,
    },
  };
}
